import { useRef, useContext } from 'react';
import { useTableSelector } from 'hooks/useTableSelector';
import { useShowAllDealers } from 'contexts/ShowAllDealersContext';
import { FACEDOWN } from 'constants/gameConstants';
import DealerChipContext from 'contexts/DealerChipContext';
import { useRegisterFloatingAnchor } from 'hooks/useFloatingRegistry';
import { DealerAnimationContext } from 'contexts/DealerAnimationContext';
import { SeatCards } from '../SeatWithPlayer/SeatCards';
import { DealerChip, SeatCardPill } from '../components';
import { getSeatDealerOffset } from '../styles';
import { useSeatAnimation, getLandscapeModeStyles } from './index';
import { SeatPosition } from 'hooks/useEllipticalPositions';
import HandRank from 'components/HandRank/HandRank';

type LandscapeOpponentProps = {
  seat: any;
  seatId: number;
  currentPlayerSeatId: number;
  isFaceCardUp: boolean;
  position: SeatPosition;
  cards: any;
  hasAction: boolean;
  playerAction: string;
  displayAction?: string;
  actionColor: any;
  isWinner: boolean;
  isTurn: boolean;
  cardPosition: any;
  holeCardCount: number;
  isRotating?: boolean;
};

export const LandscapeOpponent = ({
  seat,
  seatId,
  currentPlayerSeatId,
  isFaceCardUp,
  position,
  cards,
  hasAction,
  playerAction,
  displayAction,
  actionColor,
  isWinner,
  isTurn,
  cardPosition,
  holeCardCount,
  isRotating = false
}: LandscapeOpponentProps) => {
  const game = useTableSelector((table) => table.game);
  const { isAnimating, dealer } = useContext(DealerAnimationContext);
  const seatRef = useRef<HTMLDivElement>(null);
  const turnGlowRef = useRef<HTMLDivElement>(null);
  const opponentGlowRef = useRef<HTMLDivElement>(null);
  const dealerChipRef = useRef<HTMLDivElement>(null);

  const showAllDealers = useShowAllDealers();
  const showDealerChip = showAllDealers || (!!game.dealerVisible && seatId === dealer && !isAnimating);

  useRegisterFloatingAnchor(DealerChipContext, seatId, dealerChipRef);

  useSeatAnimation(isWinner, isTurn, false, seatRef, turnGlowRef, opponentGlowRef);
  const { getOpponentWrapperStyle, getOpponentContentStyle } = getLandscapeModeStyles(
    position,
    isRotating
  );

  const showCards = game.seatsWithVisibleCards?.includes(seatId);
  const allowShowdownReveal = !isFaceCardUp && showCards;
  const displayCards = isFaceCardUp
    ? cards
    : Array.from({ length: holeCardCount }, (_, index) => {
        const card = cards?.[index];
        return card && card !== '0' && card !== '' ? card : FACEDOWN;
      });

  const nameSize = '0.65rem';
  const stackSize = '0.65rem';

  return (
    <div style={getOpponentWrapperStyle()}>
      <div ref={seatRef} style={getOpponentContentStyle()}>
      {/* Cards above */}
      {showCards && (
        <SeatCards
          seatId={seatId}
          cards={displayCards}
          position={position}
          cardPosition={cardPosition}
          allowShowdownReveal={allowShowdownReveal}
        />
      )}

      {/* Card pill with avatar, name, stack */}
      <SeatCardPill
        seat={seat}
        seatId={seatId}
        currentPlayerSeatId={currentPlayerSeatId}
        isFaceCardUp={isFaceCardUp}
        isPortrait={false}
        hasAction={hasAction}
        playerAction={playerAction}
        displayAction={displayAction}
        actionColor={actionColor}
        isWinner={isWinner}
        nameSize={nameSize}
        stackSize={stackSize}
        cardPosition={cardPosition}
        glowRef={opponentGlowRef}
        seatLeftPct={position?.leftPct}
      />

      {/* Hand strength */}
      {isFaceCardUp && <HandRank />}

      {/* Dealer marker + positioning reference for the floating OuterDealerButton
          — see getSeatDealerOffset for the inside-the-seat, toward-centre rule. */}
      <div ref={dealerChipRef} style={{
        ...getSeatDealerOffset(position, false),
        opacity: showDealerChip ? 1 : 0,
        pointerEvents: showDealerChip ? 'auto' : 'none'
      }}>
        <DealerChip />
      </div>
      </div>
    </div>
  );
};
